import type { ReactNode } from 'react';
import { Info, MoreHorizontal } from 'lucide-react';

import { CopyButton } from '@/components/common/CopyButton';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDate } from '@/features/common/format';
import { StatusToggle } from './StatusToggle';
import { truncateId } from '../utils';
import type { PhoneNumberView } from '../types';

export interface NumbersTableProps {
  numbers: PhoneNumberView[];
  /** `GET /agent/factories` join (§6.2) — name resolution + skeleton flag. */
  agentNameById: Map<string, string>;
  factoriesLoading: boolean;
  projectNameById: Map<string, string>;
  /** Verdicts for `voice:numbers:update|bind|delete` on `org:${orgId}`. */
  canUpdate: boolean | undefined;
  canBind: boolean | undefined;
  canDelete: boolean | undefined;
  onEdit: (number: PhoneNumberView) => void;
  onBind: (number: PhoneNumberView) => void;
  onDelete: (number: PhoneNumberView) => void;
}

function TruncatedWithTooltip({
  text,
  className,
  tooltip,
}: {
  text: string;
  className?: string;
  tooltip: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{text}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Dropdown item with the P2 deny rule (design §6.3): without the policy the
 * item is disabled + Tooltip. The span trigger keeps hover events alive on the
 * disabled item.
 */
function GatedMenuItem({
  allowed,
  deniedHint,
  onSelect,
  destructive,
  children,
}: {
  allowed: boolean | undefined;
  deniedHint: string;
  onSelect: () => void;
  destructive?: boolean;
  children: ReactNode;
}) {
  const item = (
    <DropdownMenuItem
      disabled={allowed !== true}
      onSelect={allowed === true ? onSelect : undefined}
      className={destructive ? 'text-destructive focus:text-destructive' : undefined}
    >
      {children}
    </DropdownMenuItem>
  );
  if (allowed === true) {
    return item;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block">{item}</span>
      </TooltipTrigger>
      <TooltipContent>{deniedHint}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Numbers table (design §6) — column defs + client joins. Actions are
 * menu-only (F2: no row click); the Number column is never reformatted or
 * truncated (F1). Below `lg` the same data renders as the stacked card list
 * (§18): header = number + Status toggle, meta lines, trailing actions menu.
 */
export function NumbersTable({
  numbers,
  agentNameById,
  factoriesLoading,
  projectNameById,
  canUpdate,
  canBind,
  canDelete,
  onEdit,
  onBind,
  onDelete,
}: NumbersTableProps) {
  function boundAgentCell(row: PhoneNumberView) {
    if (!row.factoryId) {
      return <span className="text-sm text-muted-foreground">Not bound</span>;
    }
    const name = agentNameById.get(row.factoryId);
    if (name) {
      return <span className="text-sm">{name}</span>;
    }
    if (factoriesLoading) {
      return <SkeletonBlock className="h-4 w-24" rounded="sm" />;
    }
    // Factory not in the visible list (owner/org-manager scoping) — the id is
    // the honest fallback (F3).
    return (
      <TruncatedWithTooltip
        className="font-mono text-xs"
        text={truncateId(row.factoryId)}
        tooltip={row.factoryId}
      />
    );
  }

  function actionsMenu(row: PhoneNumberView, align: 'end' | 'center' = 'end') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span className="inline-flex h-10 w-10 items-center justify-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`Actions for ${row.number}`}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align}>
          <GatedMenuItem
            allowed={canBind}
            deniedHint="You don't have permission to bind numbers."
            onSelect={() => onBind(row)}
          >
            Bind to agent
          </GatedMenuItem>
          <GatedMenuItem
            allowed={canUpdate}
            deniedHint="You don't have permission to update numbers."
            onSelect={() => onEdit(row)}
          >
            Edit number
          </GatedMenuItem>
          <DropdownMenuSeparator />
          <GatedMenuItem
            allowed={canDelete}
            deniedHint="You don't have permission to delete numbers."
            onSelect={() => onDelete(row)}
            destructive
          >
            Delete number
          </GatedMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const agentNameOf = (row: PhoneNumberView) =>
    row.factoryId ? agentNameById.get(row.factoryId) : undefined;

  const columns: DataTableColumn<PhoneNumberView>[] = [
    {
      id: 'number',
      header: 'Number',
      cell: (row) => <span className="font-mono text-sm">{row.number}</span>,
    },
    {
      id: 'label',
      header: 'Label',
      cell: (row) =>
        row.label ? (
          <TruncatedWithTooltip
            className="block max-w-[20ch] truncate text-sm"
            text={row.label}
            tooltip={row.label}
          />
        ) : (
          <span className="text-sm text-muted-foreground" aria-hidden="true">
            —
          </span>
        ),
    },
    {
      id: 'bound-agent',
      header: 'Bound agent',
      cell: boundAgentCell,
    },
    {
      id: 'project',
      header: 'Project',
      cell: (row) =>
        row.projectId ? (
          <span className="text-sm">{projectNameById.get(row.projectId) ?? '—'}</span>
        ) : (
          <span className="text-sm text-muted-foreground" aria-hidden="true">
            —
          </span>
        ),
    },
    {
      id: 'webhook-key',
      header: (
        <span className="inline-flex items-center gap-1">
          Webhook key
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>Telephony webhook integration reference. Display only.</TooltipContent>
          </Tooltip>
        </span>
      ),
      cell: (row) =>
        row.apiKeyId ? (
          <span className="inline-flex items-center gap-1">
            <TruncatedWithTooltip
              className="font-mono text-xs"
              text={truncateId(row.apiKeyId)}
              tooltip={row.apiKeyId}
            />
            <CopyButton value={row.apiKeyId} label="Copy webhook key" />
          </span>
        ) : (
          <span className="text-sm text-muted-foreground" aria-hidden="true">
            —
          </span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <StatusToggle
          number={row}
          agentName={agentNameOf(row)}
          allowed={canUpdate}
          deniedHint="You don't have permission to update numbers."
        />
      ),
    },
    {
      id: 'registered',
      header: 'Registered',
      cell: (row) => (
        <span className="text-sm" title={row.createdAt}>
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: 'Actions',
      className: 'text-right',
      headerClassName: 'text-right',
      cell: (row) => actionsMenu(row),
    },
  ];

  return (
    <>
      <div className="hidden lg:block">
        <DataTable columns={columns} data={numbers} pageSize={10} getRowId={(row) => row.id} />
      </div>

      {/* <lg: stacked card list — same controls, same order (§18, I1/A3). */}
      <ul className="space-y-3 lg:hidden">
        {numbers.map((row) => (
          <li key={row.id} className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 break-all font-mono text-base font-medium">{row.number}</p>
              <StatusToggle
                number={row}
                agentName={agentNameOf(row)}
                allowed={canUpdate}
                deniedHint="You don't have permission to update numbers."
              />
            </div>
            <div className="mt-2 space-y-1">
              {row.label ? <p className="text-sm">{row.label}</p> : null}
              <p className="text-sm text-muted-foreground">
                Bound agent: {row.factoryId ? (agentNameOf(row) ?? row.factoryId) : 'Not bound'}
              </p>
              <p className="text-sm text-muted-foreground">
                Project: {row.projectId ? (projectNameById.get(row.projectId) ?? '—') : '—'}
              </p>
              <p className="text-sm text-muted-foreground" title={row.createdAt}>
                Registered {formatDate(row.createdAt)}
              </p>
            </div>
            <div className="mt-2 flex justify-end">{actionsMenu(row, 'center')}</div>
          </li>
        ))}
      </ul>
    </>
  );
}
